"use client";

import { useCallback, useRef, useState } from "react";

/**
 * Synchronous re-entry guard for submit/mutation handlers.
 *
 * React state guards (`sending`, `isPending`, `disabled`) flush
 * asynchronously, so a double-click or a held Enter can pass
 * `if (sending) return` twice before the first render lands. The ref
 * flips synchronously and closes that window.
 *
 * Keep any existing loading state for UI; wrap the handler body in
 * `run` for correctness. `isBusy` is provided for call sites that had
 * no loading state to drive `disabled` with.
 */
export function useInFlightGuard() {
  const inFlightRef = useRef(false);
  const [isBusy, setIsBusy] = useState(false);

  const run = useCallback(
    async <T,>(fn: () => Promise<T>): Promise<T | undefined> => {
      if (inFlightRef.current) return undefined;
      inFlightRef.current = true;
      setIsBusy(true);
      try {
        return await fn();
      } finally {
        inFlightRef.current = false;
        setIsBusy(false);
      }
    },
    [],
  );

  return { run, isBusy };
}
